import { isAxiosError } from 'axios'
import { useMemo, useState } from 'react'
import pick from 'lodash/fp/pick'
import sortBy from 'lodash/fp/sortBy'
import Mustache from 'mustache'
import {
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'

import {
  MacrosProvider,
  useMacros,
  useCreateMacro,
  useUpdateMacro,
  useDeleteMacro,
  type TextMacro,
} from '~/entities/macro'
import { Alert, Box } from '@mui/material'
import { required } from '~/shared/lib/required'

type MacroDraft = Pick<TextMacro, 'name' | 'template'> & Partial<Pick<TextMacro, 'id'>>

// Strip any leading periods and all whitespace from macro name.
const cleanMacroValue = <T extends Pick<TextMacro, 'name'>>(value: T) => ({
  ...value,
  name: value.name.replace(/^\.+/, '').replace(/\s/, ''),
})

const getNewMacro = (): MacroDraft => ({
  name: '',
  template: '',
})

// Single-brace delimiters, matching how the chat composer renders macros.
const MACRO_DELIMITERS: [string, string] = ['{', '}']

// The composer renders macros through Mustache, so a template Mustache can't
// parse crashes it. Catch that here — using the same library — so a broken
// macro can't be saved in the first place.
const templateParseError = (template: string): string | null => {
  try {
    Mustache.parse(template, MACRO_DELIMITERS)
    return null
  } catch {
    return "Check that every '{' has a matching '}' — this macro can't be saved as written."
  }
}

// Pull a user-facing message out of a 409 conflict from POST/PATCH /macros/.
// Falls back to a generic message when the server has not (yet) shipped the
// structured `{ name: [{ code, message }] }` body added in PR 2d.
const conflictMessage = (error: unknown): string | null => {
  if (!isAxiosError<{ name?: Array<string | { message?: string }> }>(error)) return null
  if (error.response?.status !== 409) return null
  const data = error.response?.data
  const first = data?.name?.[0]
  if (typeof first === 'string') return first
  if (typeof first?.message === 'string') return first.message
  return 'A macro with this name already exists'
}

interface MacroEditorRowProps {
  value?: TextMacro
  isNew?: boolean
  onFinish?: () => void
}

const MacroEditorRow = (props: MacroEditorRowProps) => {
  const createMutation = useCreateMacro()
  const updateMutation = useUpdateMacro()
  const { macros } = useMacros()

  const [value, setValue] = useState(props.value ?? getNewMacro())
  const mutation = props.isNew ? createMutation : updateMutation

  const cleanedName = useMemo(() => cleanMacroValue(value).name, [value])
  const clientDuplicate =
    cleanedName.length > 0 && macros.some(m => m.name === cleanedName && m.id !== value.id)

  // Only validate a template the user actually edited, matching the backend's
  // "changed template only" gate: a macro whose stored template predates the
  // guard (already unparseable) must still be renameable from Settings.
  const originalTemplate = props.value?.template ?? ''
  const templateError = useMemo(
    () => (value.template === originalTemplate ? null : templateParseError(value.template)),
    [value.template, originalTemplate],
  )
  const saveDisabled = clientDuplicate || cleanedName.length === 0 || !!templateError

  const serverMessage = conflictMessage(mutation.error)
  const nameError = clientDuplicate
    ? `A macro named ".${cleanedName}" already exists`
    : serverMessage

  const updateName = (next: string) => {
    setValue({ ...value, name: next })
    if (mutation.error) mutation.reset()
  }

  return (
    <TableRow style={{ verticalAlign: 'top' }}>
      <TableCell component="th" scope="row">
        <TextField
          placeholder=".macro"
          value={value.name}
          onChange={e => updateName(e.target.value)}
          error={!!nameError}
          helperText={nameError || undefined}
        />
      </TableCell>
      <TableCell style={{ whiteSpace: 'pre-wrap' }}>
        <TextField
          fullWidth
          placeholder="Add content"
          multiline
          value={value.template}
          onChange={e => setValue({ ...value, template: e.target.value })}
          error={!!templateError}
          helperText={templateError || undefined}
        />
      </TableCell>

      <TableCell align="right">
        {props.isNew ? (
          <Button
            disabled={saveDisabled}
            onClick={() => {
              createMutation.mutate(cleanMacroValue(value), {
                onSuccess: () => setValue(getNewMacro()),
              })
            }}
          >
            Add
          </Button>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              disabled={saveDisabled}
              onClick={() => {
                updateMutation.mutate(
                  {
                    id: required(value.id, 'an id on an existing macro'),
                    ...cleanMacroValue(pick(['name', 'template'], value)),
                  },
                  { onSuccess: () => props.onFinish?.() },
                )
              }}
            >
              <SaveIcon />
            </IconButton>
            <Button onClick={props.onFinish}>Cancel</Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}

const MacroDisplayRow = (props: { macro: TextMacro; onEdit: () => void }) => {
  const { mutate: deleteMacro } = useDeleteMacro()

  const row = props.macro
  return (
    <TableRow key={row.name} style={{ verticalAlign: 'top' }}>
      <TableCell component="th" scope="row" style={{ fontWeight: 700 }}>
        {'.'}
        {row.name}
      </TableCell>
      <TableCell style={{ whiteSpace: 'pre-wrap' }}>{row.template}</TableCell>

      <TableCell align="right">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={props.onEdit}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => deleteMacro(row.id)}>
            <DeleteIcon />
          </IconButton>
        </div>
      </TableCell>
    </TableRow>
  )
}

const MacroRow = (props: { macro: TextMacro }) => {
  const [editing, setEditing] = useState(false)

  return !editing ? (
    <MacroDisplayRow {...props} onEdit={() => setEditing(true)} />
  ) : (
    <MacroEditorRow {...props} value={props.macro} onFinish={() => setEditing(false)} />
  )
}

const MacroSettingsContent = () => {
  const { macros, isLoading } = useMacros()

  return (
    <Box
      sx={{
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div style={{ display: 'flex' }}>
        <Typography style={{ flex: 1 }} variant="h6">
          My Macros
        </Typography>

        {/* <TextField placeholder="Filter" /> */}
      </div>

      <Box sx={{ my: 1 }}>
        <Alert color="info" variant="outlined">
          Tip: use &apos;&#123;patient.name&#125;&apos; in a macro to use the member&apos;s
          preferred name if one exists, or their first name if not
        </Alert>
      </Box>

      <div style={{ flex: 1, overflow: 'scroll' }}>
        {!isLoading ? (
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Text</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortBy('name', macros).map(row => (
                <MacroRow key={row.id} macro={row} />
              ))}
              <MacroEditorRow isNew />
            </TableBody>
          </Table>
        ) : null}
      </div>
    </Box>
  )
}

const MacroSettings = () => (
  <MacrosProvider>
    <MacroSettingsContent />
  </MacrosProvider>
)

export default MacroSettings
